import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bell, ChevronRight, Utensils } from 'lucide-react';
import {
  ATTENDANCE_COPY,
  DEVELOPMENT_COPY,
  EMPTY_STATES,
  HOME_COPY,
  SUBSCRIPTION_COPY,
} from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { StageIcon } from '@/components/development';
import { Avatar, Card, Chip, EmptyState, ErrorNote, ReadOnlyBanner, Spinner } from '@/components/ui';
import { apiData } from '@/lib/api';
import { formatDate, greetingFor, withParticle } from '@/lib/format';
import { useAuth } from '@/store/auth';
import type { ParentHomeCard } from '@/types';

/**
 * 학부모 홈 — "오늘의 우리 아이" 카드 피드 (§7.5).
 * §7.1 가드: 기능 아이콘 바둑판 그리드를 쓰지 않는다.
 */
export function ParentHomePage() {
  const membership = useAuth((s) => s.membership);
  const userName = useAuth((s) => s.user?.name ?? '');

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['home', 'parent', membership?.facilityId],
    queryFn: () => apiData<{ date: string; children: ParentHomeCard[] }>('/home'),
  });

  const greeting = HOME_COPY[
    `greeting${greetingFor().charAt(0).toUpperCase()}${greetingFor().slice(1)}` as
      | 'greetingMorning'
      | 'greetingAfternoon'
      | 'greetingEvening'
  ];

  return (
    <Screen>
      <AppBar
        title={HOME_COPY.parentTitle}
        subtitle={`${greeting}, ${userName}님`}
        right={
          <Link
            to="/notifications"
            aria-label="알림"
            className="tap grid place-items-center rounded-full text-ink hover:bg-primary-soft"
          >
            <Bell size={20} />
          </Link>
        }
      />

      {membership && !membership.canWrite && (
        <ReadOnlyBanner message={SUBSCRIPTION_COPY.expiredBanner} />
      )}

      {isPending && <Spinner label="오늘 소식을 모으는 중이에요" />}
      {isError && <ErrorNote message="소식을 불러오지 못했어요." onRetry={() => void refetch()} />}

      {data && data.children.length === 0 && (
        <EmptyState title={EMPTY_STATES.children.title} body={EMPTY_STATES.children.body} />
      )}

      <div className="flex flex-col gap-4 p-4">
        {data?.children.map((card) => (
          <ChildCard key={card.child.id} card={card} />
        ))}
      </div>
    </Screen>
  );
}

function ChildCard({ card }: { card: ParentHomeCard }) {
  const { child } = card;

  return (
    <Card as="section" className="animate-rise-in flex flex-col gap-4">
      <header className="flex items-center gap-3">
        <Avatar name={child.name} size={48} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-lg font-bold">{child.name}</p>
          <p className="truncate text-sm text-ink-sub">
            {child.classRoom?.name ?? '반 배정 전'} · {child.age.label}
            {child.age.corrected ? ' (교정월령)' : ''}
          </p>
        </div>
        <AttendanceChip status={card.attendanceStatus} />
      </header>

      {card.todos.length > 0 && (
        <div className="rounded-sm bg-accent-soft p-3">
          <p className="mb-1.5 text-sm font-semibold text-ink">{HOME_COPY.todoTitle}</p>
          <ul className="flex flex-col gap-1">
            {card.todos.map((todo) => (
              <li key={`${todo.type}-${todo.id}`} className="text-sm text-ink">
                · {todo.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {card.dailyNote ? (
        <Link
          to={`/notes/${card.dailyNote.id}`}
          className="rounded-sm bg-bg p-3 transition-colors hover:bg-primary-soft"
        >
          <div className="mb-1 flex items-center justify-between">
            <span className="text-sm font-semibold text-primary-strong">오늘의 알림장</span>
            <span className="text-sm text-ink-sub">{formatDate(card.dailyNote.noteDate)}</span>
          </div>
          <p className="line-clamp-2 text-base text-ink">{card.dailyNote.content}</p>
          {card.dailyNote.photos.length > 0 && (
            <p className="mt-1 text-sm text-ink-sub">사진 {card.dailyNote.photos.length}장</p>
          )}
        </Link>
      ) : (
        <div className="rounded-sm bg-bg p-3">
          <p className="text-base font-semibold text-ink">{EMPTY_STATES.dailyNoteParent.title}</p>
          <p className="text-sm text-ink-sub">{EMPTY_STATES.dailyNoteParent.body}</p>
        </div>
      )}

      {card.meals.length > 0 && (
        <div className="rounded-sm bg-bg p-3">
          <p className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-ink">
            <Utensils size={15} /> 오늘 식단
          </p>
          <ul className="flex flex-col gap-0.5">
            {card.meals.map((meal) => (
              <li key={meal.id} className="text-sm text-ink-sub">
                {meal.menu.join(', ')}
                {/* 알레르기 교차 표시 (§5.7) */}
                {meal.allergens.some((a) => child.allergies?.includes(a)) && (
                  <span className="ml-1 rounded-full bg-secondary-soft px-2 py-0.5 text-[12px] text-ink">
                    알레르겐 포함
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Link
        to={`/scope?childId=${child.id}`}
        className="flex items-center gap-3 rounded-sm bg-primary-soft/70 p-3 transition-colors hover:bg-primary-soft"
      >
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-surface">
          <StageIcon level={3} size={22} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-primary-strong">
            {DEVELOPMENT_COPY.treeTitle}
          </span>
          <span className="block truncate text-sm text-ink-sub">
            {card.latestDevelopment?.summary ??
              `${withParticle(child.name, '이의', '의')} 성장 기록을 살펴보세요`}
          </span>
        </span>
        <ChevronRight size={18} className="shrink-0 text-ink-sub" />
      </Link>
    </Card>
  );
}

function AttendanceChip({ status }: { status: ParentHomeCard['attendanceStatus'] }) {
  switch (status) {
    case 'PRESENT':
      return <Chip tone="primary">{ATTENDANCE_COPY.checkedIn}</Chip>;
    case 'LATE':
      return <Chip tone="accent">{ATTENDANCE_COPY.late}</Chip>;
    case 'EARLY_LEAVE':
      return <Chip tone="accent">{ATTENDANCE_COPY.earlyLeave}</Chip>;
    case 'ABSENT':
      return <Chip tone="info">{ATTENDANCE_COPY.absent}</Chip>;
    default:
      return <Chip tone="neutral">{ATTENDANCE_COPY.notYet}</Chip>;
  }
}
